import { useState } from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Breadcrumbs,
  Button,
  Link,
  Paper,
  Typography,
} from "@mui/material";
import { LoadingFailedPage, LoadingPage } from "~/components/loading";
import {
  ArrowForward as ArrowForwardIcon,
  Home as HomeIcon,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";

import {
  ActionType,
  ClientIntegration,
  ClientIntegrationType,
  GetIntegrationsSummaryDocument,
  useDeleteClientIntegrationMutation,
  useGetClientIntegrationQuery,
} from "~/operations";
import { HostedIntegrationHeader } from "./HostedIntegrationHeader";
import { IntegrationConfigurationsAndMessages } from "../../components/configurations-and-messages/IntegrationConfigurationsAndMessages";
import { mapIntegrationRouteToTitle } from "../utils";
import { setDocumentTitle } from "~/lib/commonUtils";
import { ConfirmationDialog } from "~/components/ConfirmationDialog";
import { integrationInventoryAssetsUrl } from "~/pages/integrations/utils/integrationInventoryAssetsUrl";
import { OrganizationScope, ScopeType, SpaceScope } from "~/hooks/useScope";
import {
  getIntegrationMrn,
  getIntegrationsListHref,
} from "~/pages/integrations/utils/integrationLinksUtils";

const EXPORT_INTEGRATIONS = [
  ClientIntegrationType.AwsS3,
  ClientIntegrationType.Bigquery,
  ClientIntegrationType.GcsBucket,
  ClientIntegrationType.Postgres,
  ClientIntegrationType.Snowflake,
  ClientIntegrationType.S3,
  ClientIntegrationType.AzureBlob,
];

// these are also export integrations
export const TICKET_SYSTEM_INTEGRATIONS = [
  ClientIntegrationType.TicketSystemJira,
  ClientIntegrationType.TicketSystemEmail,
  ClientIntegrationType.TicketSystemZendesk,
  ClientIntegrationType.TicketSystemGithub,
  ClientIntegrationType.TicketSystemGitlab,
  ClientIntegrationType.TicketSystemAzureDevops,
  ClientIntegrationType.TicketSystemServiceNow,
  ClientIntegrationType.TicketSystemWebhook,
];

const IMPORT_INTEGRATIONS = [
  ClientIntegrationType.MicrosoftDefender,
  ClientIntegrationType.SentinelOne,
  ClientIntegrationType.CrowdstrikeFalcon,
];

export interface HostedIntegrationPageProps {
  scope: SpaceScope | OrganizationScope;
}

export function HostedIntegrationPage({ scope }: HostedIntegrationPageProps) {
  const { integrationTypeId, integrationId } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  let navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const integrationTitle = mapIntegrationRouteToTitle(integrationTypeId);
  const mrn: string = getIntegrationMrn({
    scope,
    integrationId,
  });

  const { data, error, loading } = useGetClientIntegrationQuery({
    variables: { mrn },
  });

  const [deleteIntegration] = useDeleteClientIntegrationMutation({
    refetchQueries: [
      {
        query: GetIntegrationsSummaryDocument,
        variables: { input: { spaceMrn: scope.mrn } },
      },
    ],
  });

  const { integration } = data?.clientIntegration || {};

  setDocumentTitle([
    integration?.name,
    integrationTitle,
    "Integrations",
    scope.name,
  ]);

  if (error) {
    return <LoadingFailedPage what="Integration" />;
  }

  if (loading || !integration) {
    return <LoadingPage what="Integration" />;
  }

  // integration
  let integrationStatus: ClientIntegration["status"] = integration.status;
  if (integration.status.toLowerCase() === "not_ready") {
    integrationStatus = "pending";
  }

  const actions = {
    deleteIntegration: () => setDialogOpen(true),
    editIntegration: () => {
      navigate(`edit?${scope.params}`);
    },
  };

  const isExportIntegration = [
    ...EXPORT_INTEGRATIONS,
    ...TICKET_SYSTEM_INTEGRATIONS,
  ].includes(integration.type);

  const isImportIntegration = IMPORT_INTEGRATIONS.includes(integration.type);

  const actionType = isExportIntegration
    ? "export"
    : isImportIntegration
      ? "import"
      : "scan";

  const handleDelete = async () => {
    try {
      await deleteIntegration({
        variables: { input: { mrn: integration.mrn } },
      });
      enqueueSnackbar(
        `Successfully removed ${integrationTitle} integration from Mondoo`,
        {
          variant: "success",
        },
      );
      navigate(getIntegrationsListHref({ scope, integrationTypeId }));
    } catch {
      enqueueSnackbar(
        `Could not remove ${integrationTitle} integration from Mondoo`,
        {
          variant: "error",
        },
      );
    }
  };

  // we have a unique situation for aws is split into two routes for viewing, hosted and serverless
  // when viewing the integrations as a list, they should all show up under the same aws route.
  const modifyIntegrationLink = () => {
    if (integrationTypeId === "aws_hosted") {
      return "aws";
    }
    return integrationTypeId;
  };

  const linkToIntegrationsList = getIntegrationsListHref({ scope });

  const breadcrumbs = [
    <Link
      key="/space/overview"
      component={RouterLink}
      to={`/${scope.type === ScopeType.Space ? "space" : "organization"}/overview?${scope.params}`}
      display="flex"
    >
      <HomeIcon fontSize="inherit" />
    </Link>,
    <Link
      key="/space/integrations"
      component={RouterLink}
      to={getIntegrationsListHref({ scope })}
    >
      Integrations
    </Link>,
    <Link
      key={`/space/integrations/${integrationTypeId}`}
      component={RouterLink}
      to={linkToIntegrationsList}
    >
      {integrationTitle}
    </Link>,
    <Typography key={1} color="text.primary">
      {integration.name}
    </Typography>,
  ];

  // Sum up the total of all asset types
  const assetCounts =
    integration.assetCounts?.reduce((acc, count) => acc + count.total, 0) || 0;

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 5 }} separator="›">
        {breadcrumbs}
      </Breadcrumbs>
      {/* Header Block */}
      <Box sx={{ mb: 6.5 }}>
        <HostedIntegrationHeader
          {...{
            integration,
            actions,
            integrationTypeId,
            ...(isExportIntegration && {
              scanAction: ActionType.RunExport,
            }),
            ...(isImportIntegration && {
              scanAction: ActionType.RunImport,
            }),
          }}
        />
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: {
            xs: "column",
            sm: "row",
          },
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          mb: 4,
        }}
      >
        <Typography variant="h5" component="h2" fontWeight={700}>
          Integration Details
        </Typography>
        {/* If the integration status is pending, we don't want to show a link to assets */}
        {/* If the integration is an export, there are no assets to link to */}
        {![...EXPORT_INTEGRATIONS, ...TICKET_SYSTEM_INTEGRATIONS].includes(
          integration.type,
        ) &&
          assetCounts > 0 &&
          integrationStatus !== "pending" && (
            <Button
              color="secondary"
              component={RouterLink}
              to={integrationInventoryAssetsUrl(scope.id, integration.mrn).href}
              endIcon={<ArrowForwardIcon />}
            >
              Show Assets
            </Button>
          )}
      </Box>
      {/* Configuration and Overview Information */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <IntegrationConfigurationsAndMessages
          {...{ integration, actionType }}
        />
      </Paper>
      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={dialogOpen}
        confirmButtonText="DELETE"
        title={`Remove ${integrationTitle} Integration`}
        content={
          <Typography>
            Are you absolutely sure you want to remove this integration? This
            operation cannot be undone.
          </Typography>
        }
        onConfirm={handleDelete}
        onClose={() => setDialogOpen(false)}
        loading={loading}
      />
    </Box>
  );
}
